/**
 * 患者情報検索 / Search Patient List page.
 * Legacy: RA2 /patients/search_patients.
 * Phase 1: search UI + row selection + bottom action bar.
 * Phase 2/3: wire action buttons to CSV / PDF (label / postcard / list) endpoints.
 */

import React, { useCallback, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AppButton } from '@/components/atoms';
import CollapsibleCard from '@/components/molecules/CollapsibleCard';
import BottomBar from '@/components/organisms/Footer';
import { ADMIN_LIST_DEFAULT_PER_PAGE } from '@/constants/admin';
import useAppToast from '@/hooks/useAppToast';
import { useClinicContext } from '@/hooks/main/useClinicContext';
import {
  useSearchPatientList,
  useSearchPatientListMasterData,
} from '@/hooks/main/useSearchPatientList';
import { usePrintSearchPatientMutation } from '@/hooks/main/usePrintSearchPatientMutation';
import { useExportSearchPatientCsvMutation } from '@/hooks/main/useExportSearchPatientCsvMutation';
import { getSearchPatientListAllIds } from '@/services/main/searchPatientList';
import { DEFAULT_FETCH_ERROR_MESSAGE, getApiErrorMessage } from '@/utils/apiError';
import type { SearchPatientListFormValues } from '@/schemas/searchPatientListSchema';
import SearchPatientListForm from './components/SearchPatientListForm';
import SearchPatientListTable from './components/SearchPatientListTable';
import type { PrintSearchPatientPayload, SearchPatientListParams } from './types';
import {
  formValuesToSearchParams,
  formValuesToSearchPayload,
  searchParamsToFormValues,
  searchParamsToPage,
} from './urlParams';

const SearchPatientListPage: React.FC = () => {
  const navigate = useNavigate();
  const toast = useAppToast();
  const { tenant, clinicNumber } = useClinicContext();

  const [urlSearchParams, setUrlSearchParams] = useSearchParams();

  const initialFormValues = useMemo(
    () => searchParamsToFormValues(urlSearchParams),
    // Hydrate once on mount — subsequent URL writes are driven by this page.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const [searchParams, setSearchParams] = useState<SearchPatientListParams | null>(() => {
    if (!initialFormValues) return null;
    return {
      ...formValuesToSearchPayload(initialFormValues),
      page: searchParamsToPage(urlSearchParams),
      per_page: ADMIN_LIST_DEFAULT_PER_PAGE,
    };
  });
  const [hasSearched, setHasSearched] = useState(() => initialFormValues !== null);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);

  const { insurerTypes, sexes, dmReasonTypes } = useSearchPatientListMasterData();
  const { rows, pagination, isLoading } = useSearchPatientList(searchParams);

  const handleSearch = useCallback(
    (values: SearchPatientListFormValues) => {
      const payload = formValuesToSearchPayload(values);
      setSearchParams({ ...payload, page: 1, per_page: ADMIN_LIST_DEFAULT_PER_PAGE });
      setHasSearched(true);
      setSelectedIds([]);
      setUrlSearchParams(formValuesToSearchParams(values), { replace: true });
    },
    [setUrlSearchParams]
  );

  const handlePageChange = useCallback(
    (page: number) => {
      setSearchParams(prev => (prev ? { ...prev, page } : prev));
      // Keep existing filter params; only flip the `page` key.
      setUrlSearchParams(
        prev => {
          const next = new URLSearchParams(prev);
          if (page > 1) next.set('page', String(page));
          else next.delete('page');
          return next;
        },
        { replace: true }
      );
    },
    [setUrlSearchParams]
  );

  const handleDmEditClick = useCallback(
    (patientId: number) => {
      navigate(`../patients/${patientId}/dminfo/edit`);
    },
    [navigate]
  );

  // 3 print mutations + 1 CSV export mutation — independent loading state per button.
  const { triggerPrint: triggerListPrint, isLoading: isListPrintLoading } =
    usePrintSearchPatientMutation({ type: 'list' });
  const { triggerPrint: triggerPostcardPrint, isLoading: isPostcardPrintLoading } =
    usePrintSearchPatientMutation({ type: 'postcard' });
  const { triggerPrint: triggerLabelPrint, isLoading: isLabelPrintLoading } =
    usePrintSearchPatientMutation({ type: 'label' });
  const { triggerExport: triggerCsvExport, isLoading: isCsvExporting } =
    useExportSearchPatientCsvMutation();

  const requireSelection = useCallback((): boolean => {
    if (selectedIds.length === 0) {
      toast.warning('受診者を選択してください。');
      return false;
    }
    return true;
  }, [selectedIds, toast]);

  const buildPrintPayload = useCallback(
    (): PrintSearchPatientPayload => ({
      patient_ids: selectedIds,
      sort_radio: searchParams?.sort_radio ?? 0,
      visit_from_yearmonth: searchParams?.visit_from_yearmonth,
      visit_to_yearmonth: searchParams?.visit_to_yearmonth,
    }),
    [selectedIds, searchParams]
  );

  const handlePrintList = useCallback(() => {
    if (!requireSelection()) return;
    triggerListPrint(buildPrintPayload());
  }, [requireSelection, triggerListPrint, buildPrintPayload]);

  const handlePrintPostcard = useCallback(() => {
    if (!requireSelection()) return;
    triggerPostcardPrint(buildPrintPayload());
  }, [requireSelection, triggerPostcardPrint, buildPrintPayload]);

  const handlePrintLabel = useCallback(() => {
    if (!requireSelection()) return;
    triggerLabelPrint(buildPrintPayload());
  }, [requireSelection, triggerLabelPrint, buildPrintPayload]);

  const handleExportCsv = useCallback(() => {
    if (!requireSelection()) return;
    triggerCsvExport(buildPrintPayload());
  }, [requireSelection, triggerCsvExport, buildPrintPayload]);

  // 全選択 cross-page: paginated results only have the current page's IDs in
  // memory, so the header checkbox needs an extra API round-trip to fetch every
  // ID matching the active filter. Strip page/per_page so the backend returns
  // ALL rows, not just one page.
  const handleSelectAllPages = useCallback(async (): Promise<number[]> => {
    if (!searchParams) return [];
    try {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { page: _p, per_page: _pp, ...filters } = searchParams;
      return await getSearchPatientListAllIds(tenant, clinicNumber, filters);
    } catch (err) {
      toast.error(getApiErrorMessage(err, DEFAULT_FETCH_ERROR_MESSAGE));
      throw err;
    }
  }, [searchParams, tenant, clinicNumber, toast]);

  const totalCount = pagination?.total ?? 0;
  const hasResults = hasSearched && rows.length > 0;

  return (
    <div className="pb-4">
      <SearchPatientListForm
        onSearch={handleSearch}
        insurerTypes={insurerTypes}
        sexes={sexes}
        dmReasonTypes={dmReasonTypes}
        isLoading={isLoading}
        totalCount={totalCount}
        initialValues={initialFormValues}
      />

      {hasSearched ? (
        <CollapsibleCard>
          <SearchPatientListTable
            data={rows}
            pagination={pagination}
            onPageChange={handlePageChange}
            onDmEditClick={handleDmEditClick}
            isLoading={isLoading}
            selectedIds={selectedIds}
            onSelectChange={setSelectedIds}
            onSelectAllPages={handleSelectAllPages}
          />
        </CollapsibleCard>
      ) : null}

      {hasResults ? (
        <BottomBar
          variant="action"
          fixed
          left={
            <div className="flex flex-wrap gap-2">
              <AppButton
                type="default"
                className="rounded"
                onClick={handlePrintList}
                loading={isListPrintLoading}
              >
                一覧印刷
              </AppButton>
              <AppButton
                type="default"
                className="rounded"
                onClick={handlePrintPostcard}
                loading={isPostcardPrintLoading}
              >
                はがき印刷
              </AppButton>
              <AppButton
                type="default"
                className="rounded"
                onClick={handlePrintLabel}
                loading={isLabelPrintLoading}
              >
                ラベル印刷
              </AppButton>
              <AppButton
                type="primary"
                className="rounded"
                onClick={handleExportCsv}
                loading={isCsvExporting}
              >
                CSV出力
              </AppButton>
            </div>
          }
        />
      ) : null}
    </div>
  );
};

export default SearchPatientListPage;
